import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { clearImpersonationSession, getImpersonationSession, type ImpersonationSession } from '../../lib/impersonation'

// `revoke` is false when the session has already expired: there is nothing
// left to end server-side, and the call would only come back 401.
async function endImpersonation(revoke: boolean): Promise<void> {
  if (revoke) {
    // Closes the audit row so the token stops working now, not at its expiry.
    // Best effort: leaving must work even if the call fails, and the token
    // is cleared from this tab either way.
    await api.post('/impersonation/end', {}).catch(() => undefined)
  }
  clearImpersonationSession()
  // Full navigation, not react-router: the rest of the app (App.tsx's
  // impersonation-aware route gate, useCurrentUser's isAuthenticated check)
  // needs to re-evaluate from scratch with no session in storage, exactly
  // the same reasoning as ImpersonationEntry.tsx's own hand-off.
  window.location.replace('/')
}

function formatRemaining(expiresAt: string): string {
  const ms = Date.parse(expiresAt) - Date.now()
  if (ms <= 0) return 'expired'
  const minutes = Math.floor(ms / 60_000)
  const seconds = Math.floor((ms % 60_000) / 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// Rendered unconditionally at the top of AppShell whenever an impersonation
// session is active -- there is no path through this app where an admin
// acting as a tenant sees the tenant's UI without this banner also being
// visible. Session state is read once at mount (AppShell only mounts after
// a fresh full-page navigation already put the session in sessionStorage --
// see ImpersonationEntry.tsx), not subscribed to live storage events: the
// only two things that can end a session (expiry, this banner's own button)
// are both handled by this component itself via the countdown effect below.
export default function ImpersonationBanner() {
  const [session] = useState<ImpersonationSession | null>(() => getImpersonationSession())
  const [remaining, setRemaining] = useState<string>(() => (session ? formatRemaining(session.expiresAt) : ''))

  useEffect(() => {
    if (!session) return
    const id = setInterval(() => {
      if (Date.parse(session.expiresAt) <= Date.now()) {
        void endImpersonation(false)
        return
      }
      setRemaining(formatRemaining(session.expiresAt))
    }, 1000)
    return () => clearInterval(id)
  }, [session])

  if (!session) return null

  return (
    <div className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950">
      <span>
        Impersonating <strong>{session.tenantSlug}</strong> as {session.adminEmail} — session expires in {remaining}
      </span>
      <button
        onClick={() => void endImpersonation(true)}
        className="shrink-0 rounded-md bg-amber-950/10 px-3 py-1 font-semibold hover:bg-amber-950/20"
      >
        End impersonation
      </button>
    </div>
  )
}
